import type {
  StoredChatMessage,
  StoredAttachment,
  StoredTombstone,
  ChatCursor,
  ChatBackupManifest,
  ImportStagingRecord,
  MessageCursor,
  MessagePage,
  VaultStorageHealth,
} from '../shell/chat/chat-contracts.js';

/**
 * src/journal/vault-db.ts
 * 
 * Single durable source of truth for the Sovereign Mesh journal.
 * Implements IndexedDB storage with compound cursor indexing, atomic deduplication,
 * numeric monotonic revision checks, binary attachment store, first-class tombstones,
 * and durable sync cursors.
 */

export interface StoredFile {
  id: string;
  name: string;
  size: number;
  encrypted: boolean;
  timestamp: number;
}

export interface StoredMessage {
  id: string;
  roomId: string;
  timestamp: number;
  encryptedPayload: Uint8Array;
}

export interface StoredIdentity {
  hnsDomain: string;
  encryptedPayload: Uint8Array;
}

export type VaultState = 'uninitialized' | 'loading' | 'ready' | 'degraded' | 'locked';

const DB_NAME = 'sovereign_mesh_vault_v2';
const DB_VERSION = 2;

export class VaultDB {
  private state: VaultState = 'ready';
  private persistenceGranted: boolean = false;

  // In-memory caches for fast query performance and test environments
  private files = new Map<string, StoredFile>();
  private legacyMessages = new Map<string, StoredMessage>();
  private identities = new Map<string, StoredIdentity>();

  // Canonical v2 stores
  private chatMessages = new Map<string, StoredChatMessage>(); // key: eventId
  private chatAttachments = new Map<string, StoredAttachment>(); // key: storageKey
  private chatTombstones = new Map<string, StoredTombstone>(); // key: targetEventId
  private chatCursors = new Map<string, ChatCursor>(); // key: stream
  private chatStaging = new Map<string, ImportStagingRecord>(); // key: stagedKey
  private chatManifests = new Map<string, ChatBackupManifest>(); // key: contentHash

  private idbPromise: Promise<IDBDatabase | null> | null = null;

  constructor() {
    if (typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined') {
      this.initIndexedDB();
      this.requestStoragePersistence();
    } else {
      this.hydrateFromLocalStorage();
    }
  }

  private async requestStoragePersistence(): Promise<void> {
    if (typeof navigator !== 'undefined' && navigator.storage && navigator.storage.persist) {
      try {
        this.persistenceGranted = await navigator.storage.persist();
      } catch {
        // Best effort
      }
    }
  }

  async getStorageHealth(): Promise<VaultStorageHealth> {
    let usageBytes = 0;
    let quotaBytes = 0;

    if (typeof navigator !== 'undefined' && navigator.storage && navigator.storage.estimate) {
      try {
        const estimate = await navigator.storage.estimate();
        usageBytes = estimate.usage || 0;
        quotaBytes = estimate.quota || 0;
      } catch {}
    }

    let state: VaultStorageHealth['state'] = 'healthy';
    if (this.state === 'locked') {
      state = 'locked';
    } else if (quotaBytes > 0 && usageBytes / quotaBytes > 0.9) {
      state = 'quota-exceeded';
    } else if (quotaBytes > 0 && usageBytes / quotaBytes > 0.75) {
      state = 'low-space';
    }

    return {
      usageBytes,
      quotaBytes,
      persistenceGranted: this.persistenceGranted,
      state,
    };
  }

  private initIndexedDB(): Promise<IDBDatabase | null> {
    if (this.idbPromise) return this.idbPromise;

    this.idbPromise = new Promise<IDBDatabase | null>((resolve) => {
      try {
        if (typeof window === 'undefined') {
          resolve(null);
          return;
        }
        let idb: IDBFactory | undefined;
        try {
          idb = window.indexedDB;
        } catch {
          this.hydrateFromLocalStorage();
          resolve(null);
          return;
        }
        if (!idb) {
          this.hydrateFromLocalStorage();
          resolve(null);
          return;
        }
        const req = idb.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = (e) => {
          const db = (e.target as IDBOpenDBRequest).result;

          // 1. chat_messages store
          if (!db.objectStoreNames.contains('chat_messages')) {
            const store = db.createObjectStore('chat_messages', { keyPath: 'eventId' });
            store.createIndex('by_room_created_event', ['roomId', 'createdAt', 'eventId'], { unique: false });
            store.createIndex('by_workspace_created', ['workspaceId', 'createdAt'], { unique: false });
          }

          // 2. chat_attachments store
          if (!db.objectStoreNames.contains('chat_attachments')) {
            db.createObjectStore('chat_attachments', { keyPath: 'storageKey' });
          }

          // 3. chat_tombstones store
          if (!db.objectStoreNames.contains('chat_tombstones')) {
            const store = db.createObjectStore('chat_tombstones', { keyPath: 'targetEventId' });
            store.createIndex('by_room', 'targetRoomId', { unique: false });
          }

          // 4. chat_cursors store
          if (!db.objectStoreNames.contains('chat_cursors')) {
            db.createObjectStore('chat_cursors', { keyPath: 'stream' });
          }

          // 5. chat_staging store
          if (!db.objectStoreNames.contains('chat_staging')) {
            db.createObjectStore('chat_staging', { keyPath: 'stagedKey' });
          }

          // 6. legacy stores
          if (!db.objectStoreNames.contains('legacy_files')) {
            db.createObjectStore('legacy_files', { keyPath: 'id' });
          }
        };

        req.onsuccess = async () => {
          const db = req.result;
          await this.hydrateFromIDB(db);
          resolve(db);
        };

        req.onerror = () => {
          console.warn('Failed to open IndexedDB, falling back to in-memory/localStorage');
          this.hydrateFromLocalStorage();
          resolve(null);
        };
      } catch (err) {
        console.warn('IndexedDB initialization exception, falling back', err);
        this.hydrateFromLocalStorage();
        resolve(null);
      }
    });

    return this.idbPromise;
  }

  private async hydrateFromIDB(db: IDBDatabase): Promise<void> {
    try {
      const tx = db.transaction(['chat_messages', 'chat_tombstones', 'chat_cursors'], 'readonly');
      
      const msgReq = tx.objectStore('chat_messages').getAll();
      msgReq.onsuccess = () => {
        const msgs = msgReq.result as StoredChatMessage[];
        for (const m of msgs) {
          this.chatMessages.set(m.eventId, m);
        }
      };

      const tombReq = tx.objectStore('chat_tombstones').getAll();
      tombReq.onsuccess = () => {
        const tombs = tombReq.result as StoredTombstone[];
        for (const t of tombs) {
          this.chatTombstones.set(t.targetEventId, t);
        }
      };

      const curReq = tx.objectStore('chat_cursors').getAll();
      curReq.onsuccess = () => {
        const cursors = curReq.result as ChatCursor[];
        for (const c of cursors) {
          this.chatCursors.set(c.stream, c);
        }
      };
    } catch (e) {
      console.warn('Failed to hydrate from IDB', e);
    }
  }

  private hydrateFromLocalStorage(): void {
    try {
      if (typeof window === 'undefined') return;
      const raw = window.localStorage?.getItem('mesh_vault_chat_messages_v2');
      if (raw) {
        const parsed: StoredChatMessage[] = JSON.parse(raw);
        for (const m of parsed) {
          this.chatMessages.set(m.eventId, m);
        }
      }
    } catch (_err) {
      // Ignored in sandboxed/restricted storage contexts
    }
  }

  private persistToLocalStorage(): void {
    try {
      if (typeof window === 'undefined') return;
      const msgs = Array.from(this.chatMessages.values());
      window.localStorage?.setItem('mesh_vault_chat_messages_v2', JSON.stringify(msgs));
    } catch (_err) {
      // Ignored in sandboxed/restricted storage contexts
    }
  }

  // --- Canonical Message Repository Methods ---

  async saveChatMessage(message: StoredChatMessage): Promise<void> {
    // 1. Check if target event has a tombstone
    if (this.chatTombstones.has(message.eventId)) {
      return; // Do not resurrect deleted message
    }

    // 2. Monotonic Numeric Revision Check: only overwrite if incoming is strictly newer revision
    const existing = this.chatMessages.get(message.eventId);
    if (existing) {
      const existingRev = typeof existing.revision === 'number' ? existing.revision : 1;
      const incomingRev = typeof message.revision === 'number' ? message.revision : 1;

      if (existingRev >= incomingRev) {
        return; // Existing is newer or identical revision
      }
    }

    this.chatMessages.set(message.eventId, { ...message });

    const db = await this.idbPromise;
    if (db) {
      try {
        const tx = db.transaction(['chat_messages'], 'readwrite');
        tx.objectStore('chat_messages').put(message);
      } catch (err) {
        console.warn('Failed to write message to IndexedDB', err);
      }
    } else {
      this.persistToLocalStorage();
    }
  }

  async getChatMessage(eventId: string): Promise<StoredChatMessage | undefined> {
    return this.chatMessages.get(eventId);
  }

  async listChatMessagesAfter(
    roomId: string,
    cursor?: MessageCursor,
    limit: number = 50
  ): Promise<MessagePage> {
    // Filter by room and exclude tombstoned messages
    const roomMsgs = Array.from(this.chatMessages.values())
      .filter((m) => m.roomId === roomId && !this.chatTombstones.has(m.eventId))
      .sort((a, b) => {
        if (a.createdAt !== b.createdAt) {
          return a.createdAt - b.createdAt;
        }
        return a.eventId.localeCompare(b.eventId);
      });

    let startIndex = 0;
    if (cursor) {
      const foundIdx = roomMsgs.findIndex(
        (m) => m.createdAt === cursor.createdAt && m.eventId === cursor.eventId
      );
      if (foundIdx >= 0) {
        startIndex = foundIdx + 1;
      } else {
        // Fallback: compound comparison (createdAt, eventId) > cursor
        startIndex = roomMsgs.findIndex(
          (m) => m.createdAt > cursor.createdAt || (m.createdAt === cursor.createdAt && m.eventId.localeCompare(cursor.eventId) > 0)
        );
        if (startIndex < 0) startIndex = roomMsgs.length;
      }
    }

    const pageSlice = roomMsgs.slice(startIndex, startIndex + limit);
    const hasMore = startIndex + limit < roomMsgs.length;

    const nextCursor = pageSlice.length > 0
      ? { createdAt: pageSlice[pageSlice.length - 1].createdAt, eventId: pageSlice[pageSlice.length - 1].eventId }
      : undefined;

    const prevCursor = startIndex > 0 && roomMsgs.length > 0
      ? { createdAt: roomMsgs[Math.max(0, startIndex - 1)].createdAt, eventId: roomMsgs[Math.max(0, startIndex - 1)].eventId }
      : undefined;

    return {
      messages: pageSlice,
      nextCursor,
      prevCursor,
      hasMore,
    };
  }

  async listChatMessagesNewer(roomId: string, sinceTimestamp: number): Promise<StoredChatMessage[]> {
    return Array.from(this.chatMessages.values())
      .filter((m) => m.roomId === roomId && m.createdAt > sinceTimestamp && !this.chatTombstones.has(m.eventId))
      .sort((a, b) => {
        if (a.createdAt !== b.createdAt) {
          return a.createdAt - b.createdAt;
        }
        return a.eventId.localeCompare(b.eventId);
      });
  }

  async getAllMessagesForRoom(roomId: string): Promise<StoredChatMessage[]> {
    return Array.from(this.chatMessages.values())
      .filter((m) => m.roomId === roomId && !this.chatTombstones.has(m.eventId))
      .sort((a, b) => {
        if (a.createdAt !== b.createdAt) {
          return a.createdAt - b.createdAt;
        }
        return a.eventId.localeCompare(b.eventId);
      });
  }

  // --- Tombstones & Deletion ---

  async saveTombstone(tombstone: StoredTombstone): Promise<void> {
    const existing = this.chatTombstones.get(tombstone.targetEventId);
    if (existing && existing.deletionRevision >= tombstone.deletionRevision) {
      return; // Existing tombstone is newer or identical
    }

    this.chatTombstones.set(tombstone.targetEventId, tombstone);
    this.chatMessages.delete(tombstone.targetEventId);

    const db = await this.idbPromise;
    if (db) {
      try {
        const tx = db.transaction(['chat_messages', 'chat_tombstones'], 'readwrite');
        tx.objectStore('chat_messages').delete(tombstone.targetEventId);
        tx.objectStore('chat_tombstones').put(tombstone);
      } catch (err) {
        console.warn('Failed to commit tombstone to IndexedDB', err);
      }
    } else {
      this.persistToLocalStorage();
    }
  }

  async isTombstoned(eventId: string): Promise<boolean> {
    return this.chatTombstones.has(eventId);
  }

  async listTombstones(): Promise<StoredTombstone[]> {
    return Array.from(this.chatTombstones.values());
  }

  async clearRoomLocal(roomId: string): Promise<void> {
    const toDelete: string[] = [];
    for (const [id, msg] of this.chatMessages.entries()) {
      if (msg.roomId === roomId) {
        toDelete.push(id);
      }
    }
    for (const id of toDelete) {
      this.chatMessages.delete(id);
    }

    const db = await this.idbPromise;
    if (db) {
      try {
        const tx = db.transaction(['chat_messages'], 'readwrite');
        const store = tx.objectStore('chat_messages');
        for (const id of toDelete) {
          store.delete(id);
        }
      } catch {}
    } else {
      this.persistToLocalStorage();
    }
  }

  // --- Attachments Store ---

  async saveStoredAttachment(attachment: StoredAttachment): Promise<void> {
    this.chatAttachments.set(attachment.storageKey, attachment);

    const db = await this.idbPromise;
    if (db) {
      try {
        const tx = db.transaction(['chat_attachments'], 'readwrite');
        tx.objectStore('chat_attachments').put(attachment);
      } catch (err) {
        console.warn('Failed to save attachment to IndexedDB', err);
      }
    }
  }

  async getStoredAttachment(storageKey: string): Promise<StoredAttachment | undefined> {
    if (this.chatAttachments.has(storageKey)) {
      return this.chatAttachments.get(storageKey);
    }

    const db = await this.idbPromise;
    if (db) {
      return new Promise((resolve) => {
        try {
          const tx = db.transaction(['chat_attachments'], 'readonly');
          const req = tx.objectStore('chat_attachments').get(storageKey);
          req.onsuccess = () => resolve(req.result as StoredAttachment | undefined);
          req.onerror = () => resolve(undefined);
        } catch {
          resolve(undefined);
        }
      });
    }
    return undefined;
  }

  // --- Durable Cursors ---

  async saveCursor(cursor: ChatCursor): Promise<void> {
    this.chatCursors.set(cursor.stream, cursor);

    const db = await this.idbPromise;
    if (db) {
      try {
        const tx = db.transaction(['chat_cursors'], 'readwrite');
        tx.objectStore('chat_cursors').put(cursor);
      } catch {}
    }
  }

  async getCursor(stream: string): Promise<ChatCursor | undefined> {
    return this.chatCursors.get(stream);
  }

  // --- Staging & Backup ---

  async saveStagingRecord(record: ImportStagingRecord): Promise<void> {
    this.chatStaging.set(record.stagedKey, record);

    const db = await this.idbPromise;
    if (db) {
      try {
        const tx = db.transaction(['chat_staging'], 'readwrite');
        tx.objectStore('chat_staging').put(record);
      } catch {}
    }
  }

  async getStagingRecord(stagedKey: string): Promise<ImportStagingRecord | undefined> {
    return this.chatStaging.get(stagedKey);
  }

  async removeStagingRecord(stagedKey: string): Promise<void> {
    this.chatStaging.delete(stagedKey);
    const db = await this.idbPromise;
    if (db) {
      try {
        const tx = db.transaction(['chat_staging'], 'readwrite');
        tx.objectStore('chat_staging').delete(stagedKey);
      } catch {}
    }
  }

  // --- Legacy Compatibility Methods ---

  getState(): VaultState {
    return this.state;
  }

  setLockedState(locked: boolean): void {
    this.state = locked ? 'locked' : 'ready';
  }

  async saveFile(file: StoredFile): Promise<void> {
    this.files.set(file.id, { ...file });
  }

  async getFile(id: string): Promise<StoredFile | undefined> {
    return this.files.get(id);
  }

  async listFiles(): Promise<StoredFile[]> {
    return Array.from(this.files.values());
  }

  async saveMessage(msg: StoredMessage): Promise<void> {
    this.legacyMessages.set(msg.id, { ...msg });
    
    const converted: StoredChatMessage = {
      eventId: msg.id,
      meshId: 'mesh-sovereign-alpha',
      workspaceId: 'ws-default',
      roomId: msg.roomId,
      authorPrincipalId: 'legacy-author',
      authorDisplayName: 'Peer',
      authorType: 'human',
      body: new TextDecoder().decode(msg.encryptedPayload),
      attachments: [],
      createdAt: msg.timestamp,
      receivedAt: msg.timestamp,
      originNodeId: 'local',
      revision: 1,
      payloadHash: `hash-${msg.id}`,
      status: 'confirmed',
    };
    await this.saveChatMessage(converted);
  }

  async getMessagesForRoom(roomId: string): Promise<StoredMessage[]> {
    const v2Msgs = await this.getAllMessagesForRoom(roomId);
    return v2Msgs.map((m) => ({
      id: m.eventId,
      roomId: m.roomId,
      timestamp: m.createdAt,
      encryptedPayload: new TextEncoder().encode(m.body),
    }));
  }

  async saveIdentity(id: StoredIdentity): Promise<void> {
    this.identities.set(id.hnsDomain, { ...id });
  }

  async getIdentity(hnsDomain: string): Promise<StoredIdentity | undefined> {
    return this.identities.get(hnsDomain);
  }

  async exportVaultDump(): Promise<{ files: number; messages: number; identities: number }> {
    return {
      files: this.files.size,
      messages: this.chatMessages.size,
      identities: this.identities.size,
    };
  }

  async factoryReset(): Promise<void> {
    this.files.clear();
    this.legacyMessages.clear();
    this.identities.clear();
    this.chatMessages.clear();
    this.chatAttachments.clear();
    this.chatTombstones.clear();
    this.chatCursors.clear();
    this.chatStaging.clear();
    this.chatManifests.clear();

    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.removeItem('mesh_vault_messages_v1');
        localStorage.removeItem('mesh_vault_chat_messages_v2');
      } catch {}
    }

    const db = await this.idbPromise;
    if (db) {
      try {
        const tx = db.transaction(
          ['chat_messages', 'chat_attachments', 'chat_tombstones', 'chat_cursors', 'chat_staging'],
          'readwrite'
        );
        tx.objectStore('chat_messages').clear();
        tx.objectStore('chat_attachments').clear();
        tx.objectStore('chat_tombstones').clear();
        tx.objectStore('chat_cursors').clear();
        tx.objectStore('chat_staging').clear();
      } catch {}
    }
  }
}

export const globalVaultDB = new VaultDB();
